import createClient, { type Middleware } from 'openapi-fetch'

import { i18n } from '@/shared/i18n'

import { toApiError } from './problem'
import type { paths } from './schema'

/**
 * Cliente HTTP do painel, tipado a partir do OpenAPI exportado pela API
 * (openapi/admin.json → `npm run api:types`).
 *
 * - Mesma origem da API: cookies de sessão Sanctum seguem automaticamente.
 * - Requisições de escrita enviam o header X-XSRF-TOKEN lido do cookie.
 * - Respostas de erro viram exceções ApiError (RFC 9457) para o TanStack Query.
 */

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export function readCookie(name: string): string | undefined {
  const prefix = `${name}=`
  const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(prefix))

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : undefined
}

/** Garante o cookie XSRF-TOKEN antes da primeira requisição de escrita (ex.: login). */
export async function ensureCsrfCookie(): Promise<void> {
  if (readCookie('XSRF-TOKEN')) return

  const response = await fetch(new URL('/sanctum/csrf-cookie', window.location.origin), {
    credentials: 'include',
  })
  if (!response.ok) throw await toApiError(response)
}

export const sessionMiddleware: Middleware = {
  onRequest({ request }) {
    request.headers.set('Accept', 'application/json')
    request.headers.set('Accept-Language', i18n.language || 'pt-BR')
    request.headers.set('X-Requested-With', 'XMLHttpRequest')

    if (UNSAFE_METHODS.has(request.method)) {
      const token = readCookie('XSRF-TOKEN')
      if (token) request.headers.set('X-XSRF-TOKEN', token)
    }

    return request
  },

  async onResponse({ response }) {
    if (!response.ok) {
      throw await toApiError(response)
    }

    return response
  },
}

export const api = createClient<paths>({
  // Absoluta para funcionar também fora do navegador (testes em jsdom/Node).
  baseUrl: `${window.location.origin}/api/v1/admin`,
  credentials: 'include',
  // Resolve o fetch a cada chamada (permite interceptação por MSW/instrumentação).
  fetch: (request) => globalThis.fetch(request),
})

api.use(sessionMiddleware)

/**
 * Endpoints públicos do tenant (sem autenticação), fora do documento OpenAPI do painel.
 */
export async function publicGet<T>(path: string): Promise<T> {
  const response = await fetch(new URL(`/api/v1/public${path}`, window.location.origin), {
    headers: { Accept: 'application/json', 'Accept-Language': i18n.language || 'pt-BR' },
  })

  if (!response.ok) throw await toApiError(response)

  return (await response.json()) as T
}

/**
 * Extrai `data` de uma resposta do openapi-fetch. Erros já viraram exceção no
 * middleware; uma resposta sem corpo aqui é bug de contrato.
 */
export function ensureData<T>(result: { data?: T }): T {
  if (result.data === undefined) {
    throw new Error('Resposta da API sem corpo.')
  }

  return result.data
}

/**
 * Corpo multipart para uploads. Uso com o openapi-fetch:
 * `api.POST(path, { body: {...} as never, bodySerializer: () => toFormData({ image: file }) })`.
 */
export function toFormData(fields: Record<string, Blob | string>): FormData {
  const data = new FormData()

  for (const [name, value] of Object.entries(fields)) {
    data.append(name, value)
  }

  return data
}
